import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { publicAPI } from '../services/api';
import tcgDataService from '../services/tcgDataService';
import { Database, Shield, Zap, BarChart3, ArrowRight, CheckCircle, Clock, ExternalLink } from 'lucide-react';

const Home = () => {
  const { isAuthenticated } = useAuth();
  const [tcgStats, setTcgStats] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [requestStats, setRequestStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sites using TCG API - add your sites here
  const featuredSites = [
    {
      name: 'TCG Placeholder',
      url: 'https://tcgplaceholder.eu/',
      description: 'Free PDF generator for trading card collectors - create placeholder cards and checklists',
      logo: 'https://tcgplaceholder.eu/icon.svg'
    }
    // Add more sites as needed:
    // {
    //   name: 'Example Site',
    //   url: 'https://example.com',
    //   description: 'Description of the site',
    //   logo: 'https://example.com/logo.png'
    // }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch comprehensive TCG data with caching
        const [stats, healthResponse] = await Promise.all([
          tcgDataService.getTCGStats().catch(() => null),
          publicAPI.health().catch(() => ({ data: null }))
        ]);

        setTcgStats(stats);
        setHealthData(healthResponse.data);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Schedule automatic revalidation every 12 hours
    const cleanup = tcgDataService.scheduleRevalidation(() => {
      // Refresh data in UI when revalidation happens
      fetchData();
    });

    return cleanup; // Cleanup interval on unmount
  }, []);

  // Fetch request statistics with live updates every 30 seconds
  useEffect(() => {
    const fetchRequestStats = async () => {
      try {
        const response = await publicAPI.getRequestStats();
        if (response.data.success) {
          setRequestStats(response.data.data);
        }
      } catch (error) {
        console.error('Failed to fetch request statistics:', error);
      }
    };

    // Initial fetch
    fetchRequestStats();

    // Set up polling every 30 seconds for live updates
    const interval = setInterval(fetchRequestStats, 30000);

    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: Database,
      title: 'Multiple TCGs',
      description: 'Access comprehensive data from Pokemon, Lorcana, and more trading card games.'
    },
    {
      icon: Shield,
      title: 'Secure & Reliable',
      description: 'API key authentication with rate limiting and IP-based access control.'
    },
    {
      icon: Zap,
      title: 'High Performance',
      description: 'Redis caching and optimized queries for fast response times.'
    },
    {
      icon: BarChart3,
      title: 'Usage Analytics',
      description: 'Detailed statistics and usage tracking for all your API calls.'
    }
  ];

  const limits = [
    { type: 'With API Key', daily: '10,000', minute: '100' },
    { type: 'Without API Key', daily: '1,000', minute: '10' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-16">
      {/* Hero Section */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl md:text-6xl">
          Trading Card Game <span className="text-primary-600">API</span>
        </h1>
        <p className="mt-6 max-w-2xl mx-auto text-xl text-gray-500">
          Access comprehensive TCG data with our fast, reliable, and secure API. 
          Get cards, sets, pricing, and more from multiple trading card games.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          {isAuthenticated ? (
            <>
              <Link to="/dashboard" className="btn btn-primary text-lg px-8 py-3">
                Go to Dashboard
              </Link>
              <Link to="/docs" className="btn btn-secondary text-lg px-8 py-3">
                View Documentation
              </Link>
            </>
          ) : (
            <>
              <Link to="/register" className="btn btn-primary text-lg px-8 py-3">
                Get Started Free
              </Link>
              <Link to="/docs" className="btn btn-secondary text-lg px-8 py-3">
                View Documentation
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Features */}
      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {features.map((feature) => (
          <div key={feature.title} className="card p-6 text-center">
            <div className="flex justify-center">
              <feature.icon className="h-12 w-12 text-primary-600" />
            </div>
            <h3 className="mt-4 text-lg font-semibold text-gray-900">{feature.title}</h3>
            <p className="mt-2 text-sm text-gray-600">{feature.description}</p>
          </div>
        ))}
      </div>

      {/* Database Statistics */}
      {tcgStats && (
        <div className="card p-8 bg-gradient-to-br from-primary-50 to-purple-50">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Database Statistics</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-primary-600 mb-2">
                {tcgStats.tcgs.length}
              </div>
              <div className="text-gray-600">Supported TCGs</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary-600 mb-2">
                {tcgStats.grandTotal.sets.toLocaleString()}
              </div>
              <div className="text-gray-600">Total Sets</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary-600 mb-2">
                {tcgStats.grandTotal.cards.toLocaleString()}
              </div>
              <div className="text-gray-600">Total Cards</div>
            </div>
          </div>
        </div>
      )}

      {/* Request Statistics */}
      {requestStats && (
        <div className="card p-8 bg-gradient-to-br from-blue-50 to-indigo-50">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Requests Served</h2>
            <div className="flex items-center text-sm text-gray-500">
              <div className="animate-pulse w-2 h-2 bg-green-500 rounded-full mr-2"></div>
              Live Updates
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">
                {requestStats.today.toLocaleString()}
              </div>
              <div className="text-gray-600">Today</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-indigo-600 mb-2">
                {requestStats.month.toLocaleString()}
              </div>
              <div className="text-gray-600">This Month</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">
                {requestStats.year.toLocaleString()}
              </div>
              <div className="text-gray-600">This Year</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-pink-600 mb-2">
                {requestStats.allTime.toLocaleString()}
              </div>
              <div className="text-gray-600">All Time</div>
            </div>
          </div>
          {requestStats.lastUpdated && (
            <div className="mt-4 pt-4 border-t border-gray-200 text-xs text-gray-500 text-center">
              Last updated: {new Date(requestStats.lastUpdated).toLocaleTimeString()}
            </div>
          )}
        </div>
      )}

      {/* Available TCGs with Statistics */}
      {tcgStats && tcgStats.tcgs.length > 0 && (
        <div className="card p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Available Trading Card Games</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tcgStats.tcgs.map((tcg) => (
              <div key={tcg.name} className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{tcg.displayName}</h3>

                {/* Real statistics */}
                <div className="grid grid-cols-2 gap-4 mb-4 pb-4 border-b border-gray-200">
                  <div>
                    <div className="text-2xl font-bold text-primary-600">{tcg.totals?.sets || 0}</div>
                    <div className="text-xs text-gray-500">Sets</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-primary-600">{(tcg.totals?.cards || 0).toLocaleString()}</div>
                    <div className="text-xs text-gray-500">Cards</div>
                  </div>
                </div>

                {/* API Endpoints */}
                <div className="space-y-2">
                  <Link
                    to={`/docs/${tcg.name}`}
                    className="flex items-center text-sm text-primary-600 hover:text-primary-700"
                  >
                    <ArrowRight className="h-4 w-4 mr-2" />
                    View Documentation
                  </Link>
                </div>

                {/* Last updated */}
                {tcg.last_updated?.cards && (
                  <div className="mt-4 pt-4 border-t border-gray-200 text-xs text-gray-500">
                    <div className="flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      Updated: {new Date(tcg.last_updated.cards).toLocaleDateString()}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sites Using TCG API */}
      {featuredSites.length > 0 && (
        <div className="card p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Sites Using TCG API</h2>
          <p className="text-gray-600 mb-6">Discover projects and applications built with our API.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredSites.map((site, index) => (
              <a
                key={index}
                href={site.url}
                target="_blank"
                rel="noopener noreferrer"
                className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow hover:border-primary-300 group"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center">
                    {site.logo ? (
                      <img
                        src={site.logo}
                        alt={site.name}
                        className="w-12 h-12 rounded-lg object-cover mr-4"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary-100 to-purple-100 flex items-center justify-center mr-4">
                        <span className="text-xl font-bold text-primary-600">
                          {site.name.charAt(0)}
                        </span>
                      </div>
                    )}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                        {site.name}
                      </h3>
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-primary-600 transition-colors" />
                </div>
                <p className="mt-3 text-sm text-gray-600">{site.description}</p>
              </a>
            ))}
          </div>
          <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-sm text-gray-600 text-center">
              Using TCG API in your project? Let us know and we'll feature it here!
            </p>
          </div>
        </div>
      )}

      {/* Rate Limits */}
      <div className="card p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Rate Limits</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {limits.map((limit) => (
            <div key={limit.type} className="border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{limit.type}</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Daily Limit:</span>
                  <span className="font-semibold">{limit.daily} requests</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Per Minute:</span>
                  <span className="font-semibold">{limit.minute} requests</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start">
            <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
            <div>
              <p className="text-sm text-blue-800">
                <strong>Need higher limits?</strong> Contact admin for custom rate limits on your API keys.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* System Status */}
      {healthData && (
        <div className="card p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">System Status</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                healthData.services?.database?.success 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {healthData.services?.database?.success ? 'Operational' : 'Issues'}
              </div>
              <p className="mt-2 text-sm text-gray-600">Database</p>
            </div>
            <div className="text-center">
              <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                healthData.services?.redis?.success 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {healthData.services?.redis?.success ? 'Operational' : 'Degraded'}
              </div>
              <p className="mt-2 text-sm text-gray-600">Cache</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                {healthData.services?.tcgs?.count || 0} TCGs
              </div>
              <p className="mt-2 text-sm text-gray-600">Available</p>
            </div>
          </div>
        </div>
      )}

      {/* CTA */}
      {!isAuthenticated && (
        <div className="card p-8 text-center bg-primary-50 border-primary-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Ready to get started?</h2>
          <p className="text-gray-600 mb-6">
            Create your free account and get instant access to our TCG API.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" className="btn btn-primary text-lg px-8 py-3">
              Create Account
            </Link>
            <Link to="/docs" className="btn btn-secondary text-lg px-8 py-3">
              Read Documentation
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;